import {configureStore} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import {combineReducers} from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistReducer,
  persistStore,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import {Configuration} from 'configuration';
import advancedSearchItemsSlice from '../services/advancedSearchItems/reducer';
import advancedSearchViewModeSlice from '../services/advancedSearchViewMode/reducer';
import agentItemsSlice from '../services/agentItems/reducer';
import bulkUpdateItemsSlice from '../services/bulkUpdateItems/reducer';
import {elasticApi} from '../services/elastic';
import headSlice from '../services/head/headSlice';
import mergeSuggestionQueueSlice from '../services/mergeSuggestions/mergeSuggestionQueueSlice';
import metadataEditSlice from '../services/metadata/metadataEditSlice';
import receivedItemsSlice from '../services/receiveItems/receivedItemsSlice';
import {mapLegacyTableSettings} from '../services/settings/functions/mapLegacyTableSettings';
import settingsSlice from '../services/settings/settingsSlice';
import workItemsSlice from '../services/workItems/workItemsSlice';
import {ebsApi} from './ebsApi';
import {localApi} from './localApi';

const reducers = {
  [ebsApi.reducerPath]: ebsApi.reducer,
  [localApi.reducerPath]: localApi.reducer,
  [elasticApi.reducerPath]: elasticApi.reducer,
  [settingsSlice.name]: settingsSlice.reducer,
  [headSlice.name]: headSlice.reducer,
  [metadataEditSlice.name]: metadataEditSlice.reducer,
  [receivedItemsSlice.name]: receivedItemsSlice.reducer,
  [bulkUpdateItemsSlice.name]: bulkUpdateItemsSlice.reducer,
  [advancedSearchItemsSlice.name]: advancedSearchItemsSlice.reducer,
  [workItemsSlice.name]: workItemsSlice.reducer,
  [agentItemsSlice.name]: agentItemsSlice.reducer,
  [advancedSearchViewModeSlice.name]: advancedSearchViewModeSlice.reducer,
  [mergeSuggestionQueueSlice.name]: mergeSuggestionQueueSlice.reducer,
};
const rootReducer = combineReducers(reducers);

const whitelist: string[] = [
  settingsSlice.name,
  bulkUpdateItemsSlice.name,
  workItemsSlice.name,
  agentItemsSlice.name,
  advancedSearchViewModeSlice.name,
  mergeSuggestionQueueSlice.name,
];

const migrations = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  1: (state: any) => {
    // Migration from version 0 to 1
    return {
      ...state,
      settings: {
        ...state.settings,
        mergeSuggestionList: {
          currentType: 'SERIES',
          showUserCreated: false,
        },
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  2: (state: any) => {
    // Migration from version 1 to 2: remove deprecated settings
    if (!state?.settings) {
      return state;
    }

    const settings = {...state.settings};
    delete settings.agents;
    if (settings.overview) {
      const overview = {...settings.overview};
      delete overview.searchVisibility;
      delete overview.columnsVisibility;
      delete overview.columnsOrder;
      delete overview.sorting;
      settings.overview = overview;
    }

    return {
      ...state,
      settings,
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  3: (state: any) => {
    // Migration from version 2 to 3: table settings replaced by datagrid
    if (!state?.settings) {
      return state;
    }

    const {tables, ...settings} = state.settings;

    return {
      ...state,
      settings: {
        ...settings,
        // Already stored datagrid settings win over converted ones
        datagrid: {...mapLegacyTableSettings(tables), ...settings.datagrid},
      },
    };
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  4: (state: any) => {
    // Migration from version 3 to 4: workMinimized replaced by workSize
    if (!state?.settings) {
      return state;
    }

    const {workMinimized, ...settings} = state.settings;

    return {
      ...state,
      settings: {
        ...settings,
        workSize: workMinimized ? 'minimized' : undefined,
      },
    };
  },
};

const persistConfig = {
  key: 'ebba',
  storage,
  whitelist,
  version: 4,
  migrate: createMigrate(migrations, {debug: Configuration.env === 'local'}),
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  reducer: persistedReducer,
  devTools: Configuration.env === 'local',
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      immutableCheck: {warnAfter: 101},
      serializableCheck: {
        warnAfter: 130,
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(ebsApi.middleware)
      .concat(localApi.middleware)
      .concat(elasticApi.middleware),
});

setupListeners(store.dispatch);

const persistor = persistStore(store);

export default {store, persistor};
